import * as stylex from "@stylexjs/stylex";
import { MoreHorizontal } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode, Ref } from "react";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, radii, space, text } from "@/design/tokens.stylex";
import Button, { IconButton, type ButtonSize, type ButtonVariant } from "./Button";
import { focusBeside } from "./focusBeside";
import Popover from "./Popover";

// More and Customize: the secondary actions of a screen, one click away. A menu
// item runs an action; a heading groups items; a separator splits groups.

export type MenuEntry = {
  label: ReactNode;
  onSelect: () => void;
  icon?: ReactNode;
  /** Quiet text on the right, e.g. a shortcut or a state. */
  hint?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** Show a check: the item is on. */
  checked?: boolean;
  /** Keep the menu open after selecting (toggles). */
  keepOpen?: boolean;
} | {
  heading: ReactNode;
} | {
  separator: true;
} | false | null | undefined;
const styles = {
  item: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x166cprz",
    kzqmXN: "xh8yej3",
    kAzted: "x21xpn4",
    kg3NbH: "x1wa2ap4",
    kaIpWk: "x1wpy2cq",
    kGuDYH: "xtu3avg",
    kMwMTN: "xgwkh9f x176rghm",
    kWkggS: "xjbqb8w x1ki36sz x15twbwe",
    kI3sdo: "x1a2a7pz",
    k9WMMc: "xdpxx8g",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  danger: {
    kMwMTN: "x1nif09i x176rghm",
    $$css: true
  }
};

/** What a custom trigger must spread onto its button. */
export interface MenuTriggerProps {
  ref: Ref<HTMLButtonElement>;
  disabled: boolean;
  "aria-haspopup": "menu";
  "aria-expanded": boolean;
  "aria-controls": string | undefined;
  onClick: () => void;
}
export interface MenuProps {
  items: readonly MenuEntry[];
  /** The trigger's text. Omit it for the round ⋯ More button. */
  label?: ReactNode;
  "aria-label"?: string;
  icon?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  align?: "start" | "end";
  disabled?: boolean;
  /** A trigger of its own, e.g. the person row in the sidebar. */
  trigger?: (props: MenuTriggerProps) => ReactNode;
}
export default function Menu({
  items,
  label,
  "aria-label": ariaLabel = "More",
  icon,
  variant = label ? "secondary" : "ghost",
  size = "md",
  align = "end",
  disabled = false,
  trigger
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const close = useCallback((reason?: "outside" | "escape") => {
    setOpen(false);
    if (reason !== "outside") triggerRef.current?.focus();
  }, []);
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => document.getElementById(panelId)?.querySelector<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)')?.focus());
  }, [open, panelId]);
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Tab") {
      // The menu is portalled to the end of <body>; continue from the trigger.
      event.preventDefault();
      setOpen(false);
      focusBeside(triggerRef.current, event.shiftKey);
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)'));
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  }
  const triggerProps: MenuTriggerProps = {
    ref: triggerRef,
    disabled,
    "aria-haspopup": "menu",
    "aria-expanded": open,
    "aria-controls": open ? panelId : undefined,
    onClick: () => setOpen(current => !current)
  };
  return <>
      {trigger ? trigger(triggerProps) : label ? <Button {...triggerProps} variant={variant} size={size} icon={icon}>
          {label}
        </Button> : <IconButton {...triggerProps} aria-label={ariaLabel} variant={variant === "primary" ? "secondary" : variant} size={size}>
          {icon ?? <MoreHorizontal size={16} />}
        </IconButton>}
      <Popover open={open} anchorRef={triggerRef} onClose={close} align={align} id={panelId}>
        <div role="menu" aria-label={typeof label === "string" ? label : ariaLabel} onKeyDown={onKeyDown}>
          {items.map((entry, index) => {
          if (!entry) return null;
          if ("separator" in entry) return <hr key={index} aria-hidden className="xjm9jq1 x3pp0ks xtwjqi1 xcw5i9 xc342km" />;
          if ("heading" in entry) return <div key={index} role="presentation" className="x1wa2ap4 xfisy6v x1qn9uzo x1hqrb6m xk50ysn x1i6qc9y">
                  {entry.heading}
                </div>;
          const checkable = entry.checked !== undefined;
          return <button key={index} type="button" role={checkable ? "menuitemcheckbox" : "menuitem"} aria-checked={checkable ? entry.checked : undefined} disabled={entry.disabled} tabIndex={-1} onClick={() => {
            if (!entry.keepOpen) close();
            entry.onSelect();
          }} {...stylex.props(recipes.bareButton, styles.item, entry.danger && styles.danger)}>
                {(entry.icon || checkable) && <span aria-hidden className="x3nfvp2 x1kky2od xl56j7k x1i6qc9y">
                    {checkable ? entry.checked ? "✓" : "" : entry.icon}
                  </span>}
                <span className="x98rzlu xeuugli">{entry.label}</span>
                {entry.hint && <span className="x1i6qc9y x7x6yu3">{entry.hint}</span>}
              </button>;
        })}
        </div>
      </Popover>
    </>;
}